/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

import { Logout } from "app/components/login/Logout";

import {
  People as PeopleIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Checklist as ChecklistIcon,
  Timelapse as TimelapseIcon,
  Password as PasswordIcon,
  LockReset as LockResetIcon,
  Insights as InsightsIcon,
  Storage as StorageIcon,
  Verified as VerifiedIcon,
  FolderIcon,
  Report as AssessmentIcon,
  School as SchoolIcon,
} from "components/icons";

import * as AppConstants from "AppConstants";
import { isServiceEnabled } from "config/featureFlags";
import { DashboardRouterFactory } from "app/components/factories/DashboardRouterFactory";
import { TeamMembersRouterFactory } from "app/components/factories/TeamMembersRouterFactory";
import { LeavePlanRouterFactory } from "app/components/factories/LeavePlanRouterFactory";
// import { TeamActivitiesRouterFactory } from "app/components/factories/TeamActivitiesRouterFactory";
import { ChangePasswordRouterFactory } from "app/components/factories/ChangePasswordRouterFactory";
import { AdminResetPasswordRouterFactory } from "app/components/factories/AdminResetPasswordRouterFactory";
import { UsageAnalyticsRouterFactory } from "app/components/factories/UsageAnalyticsRouterFactory";
import { MasterRouterFactory } from "app/components/factories/MasterRouterFactory";
import { WorkingHoursRouterFactory } from "app/components/factories/WorkingHoursRouterFactory";
import { IqmsRouterFactory } from "app/components/factories/IqmsRouterFactory";
import { ProjectRouterFactory } from "app/components/factories/ProjectRouterFactory";
import { KnowledgeRouterFactory } from "app/components/factories/KnowledgeRouterFactory";
import { SkillMatrixRouterFactory } from "app/components/factories/SkillMatrixRouterFactory";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <DashboardIcon />,
    route: "/dashboard",
    component: <DashboardRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_CLIENT, AppConstants.ROLE_ACCOUNT, AppConstants.ROLE_EMPLOYEE_MEMBER, AppConstants.ROLE_BDM],
  },
  {
    type: "divider",
    name: "Divider1",
    key: "divider1",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MEMBER, AppConstants.ROLE_BDM],
  },
  {
    type: "collapse",
    name: "Members",
    key: "members",
    icon: <PeopleIcon />,
    route: "/members",
    component: <TeamMembersRouterFactory />,
    // No BDM: HRMS-116's rewritten description says "No need to see the members menu".
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER],
  },
  {
    type: "divider",
    name: "Divider3",
    key: "divider3",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER],
  },
  {
    type: "collapse",
    name: "Skill Matrix",
    key: "skill-matrix",
    service: "skill-matrix",
    icon: <SchoolIcon />,
    route: "/skill-matrix",
    component: <SkillMatrixRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  },
  {
    type: "divider",
    name: "Divider4",
    key: "divider4",
    service: "skill-matrix",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  },
  {
    type: "collapse",
    name: "IQMS",
    key: "iqms",
    service: "iqms",
    icon: <VerifiedIcon />,
    route: "/iqms",
    component: <IqmsRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER],
  },
  {
    type: "divider",
    name: "Divider5",
    key: "divider5",
    service: "iqms",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER],
  },
  {
    type: "collapse",
    name: "Projects",
    key: "projects",
    service: "project",
    icon: <AssessmentIcon />,
    route: "/projects",
    component: <ProjectRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_CLIENT],
  },
  {
    type: "divider",
    name: "Divider6",
    key: "divider6",
    service: "project",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_CLIENT],
  },
  {
    type: "collapse",
    name: "Knowledge Base",
    key: "knowledge",
    service: "knowledge",
    icon: <FolderIcon />,
    route: "/knowledge",
    component: <KnowledgeRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  },
  {
    type: "divider",
    name: "Divider7",
    key: "divider7",
    service: "knowledge",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  },
  // {
  //   type: "collapse",
  //   name: "Activities",
  //   key: "activities",
  //   icon: <ChecklistIcon />,
  //   route: "/activities",
  //   component: <TeamActivitiesRouterFactory />,
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  // },
  // {
  //   type: "divider",
  //   name: "Divider2",
  //   key: "divider2",
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  // },
  // {
  //   type: "collapse",
  //   name: "Leave Plans",
  //   key: "member_leave_plan",
  //   icon: <TimelapseIcon />,
  //   route: "/leave-plan",
  //   component: <LeavePlanRouterFactory />,
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER],
  // },
  // {
  //   type: "divider",
  //   name: "DividerLeavePlans",
  //   key: "divider_leave_plans",
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER],
  // },
  {
    type: "collapse",
    name: "Master Data",
    key: "masters",
    icon: <StorageIcon />,
    route: "/masters",
    component: <MasterRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_MASTER_DATA_ADMIN],
  },
  {
    type: "divider",
    name: "Divider11",
    key: "divider11",
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_MASTER_DATA_ADMIN],
  },
  {
    type: "collapse",
    name: "Reset User Password",
    key: "admin-reset-password",
    icon: <LockResetIcon />,
    route: "/admin-reset-password",
    component: <AdminResetPasswordRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN],
  },
  // Usage Analytics (HRMS-322). @since NEXT_VERSION
  {
    type: "divider",
    name: "DividerUsageAnalytics",
    key: "divider-usage-analytics",
    allowedRoles: [AppConstants.ROLE_ADMIN],
  },
  {
    type: "collapse",
    name: "Usage Analytics",
    key: "usage-analytics",
    icon: <InsightsIcon />,
    route: "/usage-analytics",
    component: <UsageAnalyticsRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN],
  },
  {
    type: "divider",
    name: "DividerAdminReset",
    key: "divider-admin-reset",
    allowedRoles: [AppConstants.ROLE_ADMIN],
  },
  {
    type: "collapse",
    name: "Working Hours",
    key: "working-hours",
    icon: <TimelapseIcon />,
    route: "/working-hours",
    component: <WorkingHoursRouterFactory />,
    // A BDM sees this read-only (HRMS-116 point 6); the factory decides which view they get.
    allowedRoles: [AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_ADMIN, AppConstants.ROLE_BDM],
  },
  {
    type: "divider",
    name: "DividerWorkingHours",
    key: "divider-working-hours",
    allowedRoles: [AppConstants.ROLE_ADMIN],
  },
  {
    type: "collapse",
    name: "Activity Types",
    key: "activity-types",
    icon: <ChecklistIcon />,
    route: "/activity-types",
    component: <MasterRouterFactory />,
    allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_MASTER_DATA_ADMIN],
  },
  // Export Reports is kept last in the side panel. Members export their own timesheet only;
  // managers export their subtree; admins export any member/manager/L2 manager in the system
  // (data path in ExportReportsView).
  // No member role on this divider on purpose. Everything between the previous divider and this
  // one is admin/master-data only, so giving a member both leaves two rules stacked with no menu
  // item between them -- the double border under Dashboard in review.
  {
    type: "divider",
    name: "DividerExportReports",
    key: "divider-export-reports",
    allowedRoles: [AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_ADMIN, AppConstants.ROLE_BDM],
  },
  {
    type: "collapse",
    name: "Export Reports",
    key: "export-reports",
    icon: <AssessmentIcon />,
    route: "/export-reports",
    allowedRoles: [
      AppConstants.ROLE_EMPLOYEE_MEMBER,
      AppConstants.ROLE_EMPLOYEE_MANAGER,
      AppConstants.ROLE_ADMIN,
      AppConstants.ROLE_BDM,
    ],
  },
  // {
  //   type: "divider",
  //   name: "Divider99",
  //   key: "divider99",
  //   allowedRoles: [AppConstants.ROLE_ADMIN],
  // },
  // {
  //   type: "collapse",
  //   name: "Change Password",
  //   key: "change_password",
  //   icon: <PasswordIcon />,
  //   route: "/change-password",
  //   component: <ChangePasswordRouterFactory />,
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  // },
  // {
  //   type: "divider",
  //   name: "Divider10",
  //   key: "divider10",
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER],
  // },
  // {
  //   type: "collapse",
  //   name: "Logout",
  //   key: "logout",
  //   icon: <LogoutIcon />,
  //   route: "/logout",
  //   component: <Logout />,
  //   allowedRoles: [AppConstants.ROLE_ADMIN, AppConstants.ROLE_CLIENT, AppConstants.ROLE_EMPLOYEE_MANAGER, AppConstants.ROLE_EMPLOYEE_MEMBER, AppConstants.ROLE_CLIENT],
  // },
];

export default routes.filter((r) => !r.service || isServiceEnabled(r.service));
